// API Configuration
// Allow a local backend/mock to override the hardcoded production host.
// Same-origin by default: the frontend is served from the same host as the API,
// so any domain added in the BaoTa panel works without rebuilding.
const DEFAULT_BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';
const BASE_URL = process.env.REACT_APP_BASE_URL || DEFAULT_BASE_URL;

export const API_CONFIG = {
  // Base URLs
  BASE_URL: BASE_URL,
  API_URL: process.env.REACT_APP_API_URL || `${BASE_URL}/api`,
  SOCKET_URL: process.env.REACT_APP_SOCKET_URL || BASE_URL,
  
  // Endpoints
  ENDPOINTS: {
    AUTH: '/auth',
    USER: '/user',
    ADMIN: '/admin',
    JOBS: '/jobs',
    DEPOSIT: '/deposit',
    WITHDRAW: '/withdraw',
    BANK: '/bank',
    ADDRESS: '/address',
    CHAT: '/chat',
    SECURITY: '/security',
    SOCIAL: '/social',
    VIP: '/vip',
    TASKS: '/tasks'
  },
  
  // Helper functions
  getApiUrl: (endpoint) => {
    return `${API_CONFIG.API_URL}${endpoint}`;
  },
  
  getImageUrl: (path) => {
    if (!path) return '';
    return path.startsWith('http') ? path : `${API_CONFIG.BASE_URL}${path}`;
  }
};

export default API_CONFIG;

// ---------------------------------------------------------------------------
// 后台与会员端按域名分离：admin.* 走后台，其余走会员端。
// 同一份构建产物部署到两个站点，靠访问域名决定渲染哪一侧。
// ---------------------------------------------------------------------------
const ADMIN_HOST_PREFIX = 'admin.';

const currentHost = () => (typeof window === 'undefined' ? '' : window.location.hostname);
const currentOrigin = () => (typeof window === 'undefined' ? '' : window.location.origin);

export const isAdminHost = () => currentHost().startsWith(ADMIN_HOST_PREFIX);

export const adminOrigin = () => {
  if (process.env.REACT_APP_ADMIN_URL) return process.env.REACT_APP_ADMIN_URL.replace(/\/$/, '');
  const host = currentHost().replace(/^www\./, '');
  if (!host || host.startsWith(ADMIN_HOST_PREFIX)) return currentOrigin();
  return `${window.location.protocol}//${ADMIN_HOST_PREFIX}${host}`;
};

export const userOrigin = () => {
  if (process.env.REACT_APP_SITE_URL) return process.env.REACT_APP_SITE_URL.replace(/\/$/, '');
  const host = currentHost();
  if (!host || !host.startsWith(ADMIN_HOST_PREFIX)) return currentOrigin();
  return `${window.location.protocol}//${host.slice(ADMIN_HOST_PREFIX.length)}`;
};

// ---------------------------------------------------------------------------
// 两个站点是不同 origin，localStorage 不互通，跨域跳转时把会话随 URL 带过去。
// ---------------------------------------------------------------------------
const HANDOFF_PARAM = '_sh';

const toBase64 = (str) => {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin);
};

const fromBase64 = (b64) => {
  const bin = atob(b64);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

// 在 React 渲染前调用，把 URL 上的会话写回 localStorage 并清掉参数。
// 带过来的 user 必须是能解析的 JSON，否则宁可不写：
// 写进去一个坏值会让后续 getCurrentUser 直接把页面炸成白屏。
export const consumeSessionHandoff = () => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const payload = params.get(HANDOFF_PARAM);
  if (!payload) return;
  params.delete(HANDOFF_PARAM);
  const query = params.toString();
  const clean = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
  try {
    const data = JSON.parse(fromBase64(payload));
    if (data.t) localStorage.setItem('token', data.t);
    if (data.u) {
      const raw = typeof data.u === 'string' ? data.u : JSON.stringify(data.u);
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') localStorage.setItem('user', raw);
    }
  } catch (e) {
    // 参数损坏就当未登录，直接清掉
  }
  window.history.replaceState({}, document.title, clean);
};

export const handoffUrl = (origin) => {
  if (!origin || typeof window === 'undefined') return origin;
  const token = localStorage.getItem('token');
  if (!token) return origin;
  const payload = toBase64(JSON.stringify({ t: token, u: localStorage.getItem('user') }));
  const sep = origin.indexOf('?') === -1 ? '?' : '&';
  return `${origin}${sep}${HANDOFF_PARAM}=${encodeURIComponent(payload)}`;
};
